import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { OrganizationType } from 'modules/Common/utils/Utility';
import CgPopover from 'modules/Common/ui/CgPopover';
import { useFreemium } from 'modules/Common/freemium/FreemiumContext';
import NotificationBubble from 'modules/Common/ui/NotificationBubble';
import { useFetchOperatorInviteCount } from 'modules/Operator/hooks/useFetchOperatorInviteCount';
import useConsultantOverviewStore from 'modules/Consultant/stores/consultantOverviewStore';
import TrialBanner from 'modules/App/components/TrialBanner';
import NavLanguageSwitcher from 'modules/App/components/NavLanguageSwitcher';
import UserMenu from 'modules/App/components/UserMenu';
import { isLanguageSelectionAllowed } from 'modules/Common/config/languageAccess';
import { useIsFeatureLocked } from 'modules/Common/config/lockedFeatures';
import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';
import { isFreeSupplierPlan } from 'modules/FreeSupplier/planAccess';
import { useSupplierInbox } from 'modules/FreeSupplier/useSupplierInbox';
import {
  FREE_SUPPLIER_NAV_ITEMS,
  getSupplierInboxNotification,
} from 'modules/FreeSupplier/supplierNavigation';
import { useFetchDeclarant } from 'modules/Declarant/hooks/useFetchDeclarant';
import { isErpIntegrationAvailable } from 'modules/Declarant/config/erpIntegration';
import { isUkRegulation } from 'modules/Common/utils/regulation';
import { useBranding } from '../hooks/useBranding';
import { SECTION_KICKERS, translateSectionKicker } from '../config/sectionKickers';
import { useTranslation } from 'react-i18next';
// Reuse the shared support surface (Calendly call + prefilled email) rather
// than duplicating the header's copy/URLs. Same modal the DiagramEditor ribbon's
// Help tab opens; mirrors the header help dropdown's "Contact Us".
import ContactSupportModal from 'modules/Diagrams/DiagramEditor/ContactSupportModal';
import OrgProfileHost from './OrgProfileHost';
import { useOrgProfileStore } from '../stores/orgProfileStore';
import { useWhatsNewUnread } from 'modules/WhatsNew/hooks/useWhatsNewUnread';
import { performOrgSwitch } from '../orgSwitchStore';
import logoFallback from 'assets/icons/logo.png';
import { PRIMARY_COLOR } from 'modules/Common/utils/brandColor';
import RecentEngagementsNav from 'modules/Verifier/components/RecentEngagementsNav';
import RecentClientsNav from 'modules/Consultant/components/RecentClientsNav';

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_COLLAPSED_WIDTH = 56;
// The sidebar docks flush against the viewport's left edge (0 gap) so it reads
// as integrated app chrome rather than a detached floating panel. Kept as a
// named constant because AppLayout folds it into the content-margin math.
export const SIDEBAR_LEFT_MARGIN = 0;
const SECTIONS_KEY = 'navSidebarSectionsCollapsed';

// === Visual primitives ===========================================================

const NavItem = ({
  icon,
  label,
  url,
  isActive,
  isCollapsed,
  notification,
  badge,
  isNew,
  onMouseEnter,
}) => {
  const { t } = useTranslation('app');
  return (
    <Link
      to={url}
      onMouseEnter={onMouseEnter}
      title={isCollapsed ? label : undefined}
      data-nav-item="true"
      className={`
      group relative flex items-center rounded-lg
      transition-colors duration-150
      ${
        isActive
          ? 'bg-primaryColor/10 text-primaryColor-dark font-semibold'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
      }
      ${isCollapsed ? 'justify-center py-2 px-0' : 'gap-2.5 px-2.5 py-1.5'}
    `}
    >
      {isActive && (
        <span
          className="absolute left-[-6px] top-1.5 bottom-1.5 w-[3px] rounded-r-full"
          style={{ backgroundColor: PRIMARY_COLOR }}
          aria-hidden="true"
        />
      )}
      <div className="relative flex items-center shrink-0">
        <FontAwesomeIcon
          icon={icon}
          className={`w-3.5 h-3.5 ${isActive ? 'text-primaryColor-dark' : 'text-slate-400 group-hover:text-slate-600'}`}
        />
        {notification && (
          <div className={`absolute ${isCollapsed ? '-top-1 -right-1' : '-top-1 -right-2'}`}>
            <NotificationBubble
              count={notification.count}
              title={notification.title}
              subtitle={notification.subtitle}
              sections={notification.sections}
              className="scale-75"
            />
          </div>
        )}
        {isCollapsed && badge && (
          <span
            className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: badge.dotColor || '#EF9F27' }}
            aria-hidden="true"
          />
        )}
      </div>
      {!isCollapsed && (
        <>
          <span className="text-left min-w-0 truncate flex-1">{label}</span>
          {badge && (
            <span
              className="shrink-0 text-[10px] font-semibold px-1.5 py-[1px] rounded-full"
              style={{
                backgroundColor: badge.bg || '#FAEEDA',
                color: badge.color || '#854F0B',
              }}
            >
              {badge.label}
            </span>
          )}
          {isNew && (
            <span className="shrink-0 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wide bg-primaryColor/15 text-primaryColor-dark rounded-md">
              {t('nav.newBadge', 'New')}
            </span>
          )}
        </>
      )}
    </Link>
  );
};

const SectionHeader = ({ label, count, isOpen, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    data-section-header="true"
    className="w-full flex items-center gap-1.5 px-2 py-1 mt-2 rounded-md text-left hover:bg-slate-50 transition-colors"
  >
    <FontAwesomeIcon
      icon={isOpen ? faIcons.faChevronDown : faIcons.faChevronRight}
      className="w-2.5 h-2.5 text-slate-400"
    />
    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex-1">
      {label}
    </span>
    {typeof count === 'number' && (
      <span className="text-[10px] text-slate-300 font-normal">{count}</span>
    )}
  </button>
);

const Divider = () => (
  <div
    className="h-px bg-slate-100 mx-2 my-2"
    aria-hidden="true"
  />
);

// Group separator for the collapsed rail. With no section labels to break
// the icon column, this needs to actually read as a divider — slate-100 is
// invisible on white, so use slate-200 with real vertical breathing room.
const CollapsedDivider = () => (
  <div
    className="h-px bg-slate-200 mx-2 my-2"
    aria-hidden="true"
  />
);

// === Org switcher ================================================================

const orgInitials = (name) => {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
};

// Deterministic avatar color per org name so the same org always picks the
// same swatch even between sessions.
const ORG_AVATAR_PALETTE = ['#534AB7', '#378ADD', '#1D9E75', '#D85A30', '#993556', '#BA7517'];
const orgAvatarColor = (id) => {
  if (!id) return ORG_AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return ORG_AVATAR_PALETTE[Math.abs(hash) % ORG_AVATAR_PALETTE.length];
};

const OrgAvatar = ({ org, size = 28 }) => {
  // Prefer Clerk's hosted org logo (imageUrl) when available so customer-
  // branded orgs render their own mark. Falls back to a deterministic
  // initials chip with a per-org-id color so the same org always picks
  // the same swatch even between sessions.
  if (org?.hasImage && org?.imageUrl) {
    return (
      <img
        src={org.imageUrl}
        alt=""
        className="shrink-0 rounded-md object-cover bg-slate-100"
        style={{ width: size, height: size }}
        aria-hidden="true"
      />
    );
  }
  const color = orgAvatarColor(org?.id);
  const initials = orgInitials(org?.name);
  return (
    <span
      className="inline-flex items-center justify-center font-semibold text-white shrink-0 rounded-md"
      style={{
        width: size,
        height: size,
        background: color,
        fontSize: Math.max(10, size * 0.38),
      }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
};

const OrgSwitcherMenu = ({ active, memberships, onSelect, onManage, hasNextPage, fetchNext }) => {
  const { t } = useTranslation('app');
  const [query, setQuery] = useState('');
  // Unread release notes live behind "Manage organization" (D9), so the count
  // is surfaced on the control that opens it. Zero when the endpoint is absent
  // (D3), which is what keeps this invisible until the backend ships.
  const { unreadCount } = useWhatsNewUnread();

  // Memberships arrive paginated from Clerk — pull the remaining pages
  // while the menu is open so search covers every org, not just the
  // first page. `memberships.length` must stay in the deps: `fetchNext`
  // is referentially stable and `hasNextPage` holds `true` across
  // intermediate pages, so without it the effect fires once and the
  // list stalls after the second page.
  useEffect(() => {
    if (hasNextPage && fetchNext) fetchNext();
  }, [hasNextPage, fetchNext, memberships.length]);

  const others = (memberships || []).filter((m) => m.organization?.id !== active?.id);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? others.filter((m) => (m.organization?.name || '').toLowerCase().includes(q))
    : others;

  return (
    <div className="-mx-3 -my-2 w-[260px]">
      <div className="px-3 pt-3 pb-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 m-0">
          {t('nav.orgSwitcher.current', 'Current')}
        </p>
      </div>
      <div className="px-2 py-1">
        <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg bg-slate-50">
          <OrgAvatar
            org={active}
            size={30}
          />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="text-[13px] font-semibold text-slate-900 m-0 truncate">
              {active?.name || t('nav.orgSwitcher.noOrganization', 'No organization')}
            </p>
            <p className="text-[10px] text-slate-500 m-0">
              {active?.publicMetadata?.type
                ? `${active.publicMetadata.type.charAt(0).toUpperCase()}${active.publicMetadata.type.slice(1)}`
                : t('nav.orgSwitcher.workspace', 'Workspace')}
            </p>
          </div>
          <FontAwesomeIcon
            icon={faIcons.faCheck}
            className="text-primaryColor text-[12px]"
          />
        </div>
      </div>

      {others.length > 0 && (
        <>
          <div className="px-3 pt-2 pb-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 m-0">
              {t('nav.orgSwitcher.switchTo', 'Switch to')}
            </p>
          </div>
          <div className="px-2 pb-1">
            <div className="relative">
              <FontAwesomeIcon
                icon={faIcons.faMagnifyingGlass}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-300 pointer-events-none"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('nav.orgSwitcher.searchPlaceholder', 'Search organizations…')}
                aria-label={t('nav.orgSwitcher.searchAriaLabel', 'Search organizations')}
                className="w-full text-[12px] pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 bg-slate-50 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primaryColor/40 focus:border-primaryColor/40 focus:bg-white transition-colors"
              />
            </div>
          </div>
          <div className="px-2 py-1 max-h-[280px] overflow-y-auto">
            {filtered.length === 0 && (
              <p className="text-[11px] text-slate-400 italic px-2 py-2 m-0">
                {t('nav.orgSwitcher.noMatches', 'No organizations match “{{query}}”', {
                  query: query.trim(),
                })}
              </p>
            )}
            {filtered.map((m) => {
              const o = m.organization;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => onSelect(o.id)}
                  className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-slate-50 transition-colors"
                >
                  <OrgAvatar
                    org={o}
                    size={30}
                  />
                  <div className="min-w-0 flex-1 leading-tight">
                    <p className="text-[13px] font-medium text-slate-900 m-0 truncate">{o.name}</p>
                    <p className="text-[10px] text-slate-500 m-0">
                      {o.publicMetadata?.type
                        ? `${o.publicMetadata.type.charAt(0).toUpperCase()}${o.publicMetadata.type.slice(1)}`
                        : t('nav.orgSwitcher.workspace', 'Workspace')}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="h-px bg-slate-100 mx-2 my-1" />
      {onManage && (
        <button
          type="button"
          onClick={onManage}
          className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <FontAwesomeIcon
            icon={faIcons.faCog}
            className="text-slate-400 text-[12px]"
          />
          {t('nav.orgSwitcher.manageOrganization', 'Manage organization')}
          {/* Suppressed at zero — a badge reading "0" is noise, not news. */}
          {unreadCount > 0 ? (
            <span
              className="ms-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold"
              aria-label={t('whatsNew.unreadBadge', '{{count}} unread update', {
                count: unreadCount,
                defaultValue_other: '{{count}} unread updates',
              })}
            >
              {unreadCount}
            </span>
          ) : null}
        </button>
      )}
    </div>
  );
};

// === Search palette stub =========================================================

const SearchBar = ({ allRoutes, isCollapsed, onSelect }) => {
  const { t } = useTranslation('app');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useMemo(() => {
    if (!query) return allRoutes.slice(0, 8);
    const q = query.toLowerCase();
    return allRoutes
      .filter((r) => r.label.toLowerCase().includes(q) || r.url.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, allRoutes]);

  // No search affordance in the collapsed rail — the parent gates this
  // component behind `!isCollapsed`, so this is defense-in-depth. The
  // ⌘K listener above still only drives the expanded popover.
  if (isCollapsed) {
    return null;
  }

  return (
    <CgPopover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      position="bottom"
      sideOffset={4}
      triggerElement={
        <button
          type="button"
          className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-[12px] text-slate-500 hover:bg-white hover:border-slate-300 transition-colors"
        >
          <FontAwesomeIcon
            icon={faIcons.faSearch}
            className="text-[12px]"
          />
          <span>{t('nav.search.trigger', 'Search or jump to…')}</span>
          <span
            className="ml-auto text-[10px] text-slate-400 bg-white border border-slate-200 rounded px-1 py-[1px] font-mono"
            style={{ fontFamily: 'ui-monospace, monospace' }}
          >
            ⌘K
          </span>
        </button>
      }
      popoverContent={
        <div className="-mx-3 -my-2 w-[260px]">
          <div className="px-2 py-2 border-b border-slate-100">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('nav.search.placeholder', 'Jump to a page…')}
              className="w-full text-[13px] px-2 py-1.5 bg-transparent focus:outline-none placeholder:text-slate-400"
            />
          </div>
          <div className="py-1 max-h-[320px] overflow-y-auto">
            {results.length === 0 ? (
              <p className="text-[11px] text-slate-400 px-3 py-2 m-0">
                {t('nav.search.noMatches', 'No matches.')}
              </p>
            ) : (
              results.map((r) => (
                <button
                  key={r.url}
                  type="button"
                  onClick={() => {
                    onSelect(r.url);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-slate-700 hover:bg-slate-50 transition-colors text-left"
                >
                  <FontAwesomeIcon
                    icon={r.icon || faIcons.faArrowRight}
                    className="text-slate-400 text-[11px] w-3"
                  />
                  <span className="flex-1 truncate">{r.label}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{r.url}</span>
                </button>
              ))
            )}
          </div>
        </div>
      }
    />
  );
};

// Placeholder for the regime-dependent sections while the declarant's
// regulation resolves — neutral rows, so neither regime's menu is implied.
const SKELETON_ROW_WIDTHS = ['w-24', 'w-20', 'w-28', 'w-20', 'w-24', 'w-16'];
const NavSectionsSkeleton = ({ isCollapsed }) => (
  <div
    className="mt-3 space-y-1 animate-pulse"
    aria-busy="true"
    data-testid="nav-sections-skeleton"
  >
    {SKELETON_ROW_WIDTHS.map((width, i) => (
      <div
        key={i}
        className={`flex items-center py-1.5 ${isCollapsed ? 'justify-center' : 'gap-2.5 px-2.5'}`}
      >
        <div className="h-4 w-4 rounded bg-slate-200" />
        {!isCollapsed && <div className={`h-3 rounded bg-slate-200 ${width}`} />}
      </div>
    ))}
  </div>
);

// === Main component ==============================================================

export const NavSidebar = ({ isSidebarHidden, isCollapsed, onToggleCollapse, onPrefetchRoute }) => {
  const history = useHistory();
  const { t, i18n } = useTranslation('app');
  const language = i18n.language;
  const { hasExceededLimit } = useFreemium();
  const { organization } = useOrganization();
  const { me, status: permissionsStatus } = usePermissionsContext();
  const brand = useBranding();
  // 'carbonglance' is the default brand key (useBranding getBrandFromEnv);
  // anything else — Faradai hostname, REACT_APP_WHITELABEL build, or a
  // partner-embed URL override — is a whitelabel.
  const isWhitelabel = !!brand.key && brand.key !== 'carbonglance';
  const location = history.location;

  const userType = organization?.publicMetadata?.type;
  const teamManagementLocked = useIsFeatureLocked('team-management');
  const hasFreeSupplierPlan = isFreeSupplierPlan(me);

  // Declarant feature flags (e.g. isCustomsRepresentative gates the
  // Companies nav item). Only fetched for declarant orgs.
  const { data: declarant, isLoading: isDeclarantLoading } = useFetchDeclarant({
    enabled: userType === OrganizationType.Declarant,
  });
  // The declarant menu is regime-specific and `isUkRegulation` reads an absent
  // declarant as EU, so building it before GET /declarant answers paints the
  // EU menu for a UK declarant and then swaps it. Hold the regime-dependent
  // sections back until the answer is in. A failed fetch ends the wait and
  // falls back to the EU menu, same as before.
  const isRegimePending =
    userType === OrganizationType.Declarant && !hasFreeSupplierPlan && isDeclarantLoading;

  const { setActive, userMemberships } = useOrganizationList({
    // Clerk caps a single page at 500; 200 keeps the switcher's client-side
    // search covering every org in one request for typical accounts.
    userMemberships: { infinite: true, pageSize: 200 },
  });
  const { data: inviteData } = useFetchOperatorInviteCount(userType, {
    enabled: permissionsStatus === 'ready' && !hasFreeSupplierPlan,
  });
  const { data: supplierInbox } = useSupplierInbox({
    enabled: permissionsStatus === 'ready' && hasFreeSupplierPlan,
  });
  const { consultantOrgId, clearConsultantOrgId } = useConsultantOverviewStore();

  // === Notification data ==========================================================
  const getNotificationData = (inviteData) => {
    if (!inviteData || inviteData.totalCount === 0) return null;
    const sections = [];
    if (Array.isArray(inviteData.declarantData) && inviteData.declarantData.length > 0) {
      sections.push({
        title: t('nav.invites.declarantRequests', 'Declarant Requests'),
        count: inviteData.declarantCount,
        color: 'bg-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        // S3 supplier-inbox items: `{ declarant: { id, name }, ... }` — the
        // inbox contract carries no declarant address, so no country flag.
        items: inviteData.declarantData.map((item) => ({
          name: item.declarant?.name || t('nav.invites.unknownDeclarant', 'Unknown Declarant'),
          country: {},
        })),
      });
    }
    if (Array.isArray(inviteData.operatorData) && inviteData.operatorData.length > 0) {
      sections.push({
        title: t('nav.invites.operatorRequests', 'Operator Requests'),
        count: inviteData.operatorCount,
        color: 'bg-green-500',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        items: inviteData.operatorData.map((item) => ({
          name: item.senderId?.name || t('nav.invites.unknownOperator', 'Unknown Operator'),
          country: {
            name: item.senderId?.address?.country?.name,
            iso2: item.senderId?.address?.country?.iso2,
            iso3: item.senderId?.address?.country?.iso3,
          },
        })),
      });
    }
    return {
      count: inviteData.totalCount,
      title: t('nav.invites.pendingRequests', 'Pending Requests'),
      // i18next plural (_one/_other). The old ternary was `> 1`, which renders
      // "0 pending requests" correctly only by accident and would be wrong in
      // any language whose zero takes the singular.
      subtitle: t('nav.invites.pendingCount', {
        count: inviteData.totalCount,
        defaultValue: '{{count}} pending request',
        defaultValue_other: '{{count}} pending requests',
      }),
      sections,
    };
  };

  // === Workflow-grouped menu config ==============================================
  // Each top-level group is now a collapsible Section (was: SubMenu). Items
  // are always visible when their section is expanded — no extra click to
  // see destinations.
  const getMenuConfig = () => {
    const baseInvitesNotification = getNotificationData(inviteData);

    // Section headers come from the shared kicker map so the sidebar and every
    // page eyebrow stay in lockstep across languages.
    const section = (name) => translateSectionKicker(SECTION_KICKERS[name], t);

    // A verification body (verifier-firm-foundation). Its own workspace only: none of the
    // customer menus apply.
    if (userType === OrganizationType.Verifier) {
      return {
        pinned: [
          {
            label: t('nav.verifier.engagements', 'Engagements'),
            icon: faIcons.faClipboardCheck,
            url: '/verifier',
            // '/verifier' prefixes every verifier URL; Engagements owns only home + engagements.
            isActive: (pathname) =>
              pathname === '/verifier' || pathname.startsWith('/verifier/engagements'),
            // Recently opened engagements, nested under their parent (workspace-ux W6).
            renderBelow: (collapsed) => <RecentEngagementsNav isCollapsed={collapsed} />,
          },
          {
            label: t('nav.verifier.team', 'Team'),
            icon: faIcons.faUserFriends,
            url: '/verifier/team',
          },
          {
            label: t('nav.verifier.firm', 'Firm profile'),
            icon: faIcons.faBuilding,
            url: '/verifier/firm',
          },
        ],
        sections: [],
      };
    }

    if (hasFreeSupplierPlan) {
      const supplierInboxNotification = getSupplierInboxNotification(supplierInbox?.items);
      return {
        pinned: [
          ...FREE_SUPPLIER_NAV_ITEMS.map((item) => ({
            label: item.label,
            icon: faIcons[item.icon],
            url: item.url,
            notification: item.notification === 'requests' ? supplierInboxNotification : undefined,
          })),
          ...(teamManagementLocked || !me?.isAdmin
            ? []
            : [
                {
                  label: t('nav.freeSupplier.team', 'Team'),
                  icon: faIcons.faUserFriends,
                  url: '/team/manage',
                },
              ]),
        ],
        sections: [],
      };
    }

    if (userType === OrganizationType.Declarant) {
      // Regime gate (CG-2312, docs/features/uk-cbam-imports/00-OVERVIEW.md D1):
      // a declarant is entirely UK or entirely EU, so the sidebar renders one
      // regime's menu, never a mix. UK trims the EU-only surfaces (Benchmarks,
      // certificate/quote analytics) that have no UK equivalent yet.
      const isUk = isUkRegulation(declarant);
      return {
        pinned: [
          { label: t('nav.declarant.home', 'Home'), icon: faIcons.faHome, url: '/overview' },
          {
            label: t('nav.declarant.uploadHub', 'Upload hub'),
            icon: faIcons.faCloudUploadAlt,
            url: '/upload-hub',
            new: true,
          },
          {
            label: t('nav.declarant.downloadHub', 'Download hub'),
            icon: faIcons.faCloudDownloadAlt,
            url: '/download-hub',
            new: true,
          },
        ],
        sections: [
          {
            key: 'manage',
            label: section('MANAGE'),
            items: [
              {
                label: t('nav.declarant.imports', 'Imports'),
                icon: faIcons.faShip,
                url: isUk ? '/uk/imports' : '/imports',
              },
              // Goods bought, without customs data (docs/features/purchases).
              // Declarant-only: the operator's "Purchases" is a different
              // record (stock purchase orders) on a different sidebar.
              {
                label: t('nav.declarant.purchases', 'Purchases'),
                icon: faIcons.faCartShopping,
                url: '/purchases',
              },
              // Same building icon as the supplier details header — one
              // glyph for the supplier entity everywhere.
              {
                label: t('nav.declarant.suppliers', 'Suppliers'),
                icon: faIcons.faBuilding,
                url: '/suppliers',
              },
              {
                label: t('nav.declarant.installations', 'Installations'),
                icon: faIcons.faIndustry,
                url: '/installations',
              },
              {
                label: t('nav.declarant.emissions', 'Emissions'),
                icon: faIcons.faCloud,
                url: '/emissions',
              },
              // Companies is a customs-representative feature — declarants
              // managing third-party importers. Hidden for everyone else
              // (the /companies route is gated the same way).
              ...(declarant?.isCustomsRepresentative
                ? [
                    {
                      label: t('nav.declarant.companies', 'Companies'),
                      icon: faIcons.faBuilding,
                      url: '/companies',
                    },
                  ]
                : []),
              // EU-only: benchmarks are the EU default-values dataset.
              ...(isUk
                ? []
                : [
                    {
                      label: t('nav.declarant.benchmarks', 'Benchmarks'),
                      icon: faIcons.faBalanceScale,
                      url: '/benchmarks',
                      new: true,
                    },
                  ]),
            ],
          },
          {
            key: 'analyze',
            label: section('ANALYZE'),
            items: [
              {
                label: t('nav.declarant.analyticsOverview', 'Overview'),
                icon: faIcons.faChartPie,
                url: '/analytics-overview',
              },
              // EU-only analytics: certificate pricing and Cost radar's
              // Default Costs are built on the EU certificate market. UK gets
              // the Quoting Map on its own route instead — the half of Cost
              // radar that is regime-aware (uk-quoting-and-quotes D1). EU
              // keeps reaching it as Cost radar's quoting mode.
              ...(isUk
                ? [
                    {
                      label: t('nav.declarant.quotingMap', 'Quoting map'),
                      icon: faIcons.faMapLocationDot,
                      url: '/quoting-map',
                      new: true,
                    },
                    // The UK cost calculator: a what-if on HMRC's defaults and
                    // rates for any good (uk-cost-calculator-widget phase 4).
                    {
                      label: t('nav.declarant.costCalculator', 'Cost calculator'),
                      icon: faIcons.faCalculator,
                      url: '/uk/cost-calculator',
                      new: true,
                    },
                  ]
                : [
                    {
                      label: t('nav.declarant.certificatePrice', 'Certificate price'),
                      icon: faIcons.faEuroSign,
                      url: '/carbon-price-tracker',
                    },
                    {
                      label: t('nav.declarant.costRadar', 'Cost radar'),
                      icon: faIcons.faBullseye,
                      url: '/cost-radar',
                      new: true,
                    },
                  ]),
              {
                label: t('nav.declarant.costs', 'Costs'),
                icon: faIcons.faCoins,
                // Same item, the regime's own route — the pattern the Imports
                // item above already uses.
                url: isUk ? '/uk/costs' : '/cbam-costs',
                // '/cbam-costs' prefixes the Hedging page, which has its own item.
                isActive: (pathname) => {
                  const url = isUk ? '/uk/costs' : '/cbam-costs';
                  return (
                    !pathname.startsWith('/cbam-costs/hedging') &&
                    (pathname === url || pathname.startsWith(`${url}/`))
                  );
                },
              },
              // The certificate account beside the bill (certificate-management D1),
              // and price risk & hedging (cbam-hedging-page), EU-only like its route.
              ...(isUk
                ? []
                : [
                    {
                      label: t('nav.declarant.certificates', 'Certificates'),
                      icon: faIcons.faCertificate,
                      url: '/cbam-certificates',
                      new: true,
                    },
                    {
                      label: t('nav.declarant.hedging', 'Hedging'),
                      icon: faIcons.faShieldHalved,
                      url: '/cbam-costs/hedging',
                      new: true,
                    },
                  ]),
              // Both regimes: the quote pages read the regime for years,
              // goods, price and currency (uk-quoting-and-quotes).
              {
                label: t('nav.declarant.quotes', 'Quotes'),
                icon: faIcons.faReceipt,
                url: '/cbam-quotes',
                new: true,
              },
            ],
          },
          {
            key: 'reports',
            label: section('REPORTS'),
            items: [
              {
                // A UK declarant files a RETURN with HMRC, not a report with
                // the CBAM Registry — the page says so, and the item that
                // leads there has to agree (uk-cbam-reports R7).
                label: isUk
                  ? t('nav.declarant.ukReturns', 'Returns')
                  : t('nav.declarant.reports', 'Reports'),
                icon: faIcons.faFileAlt,
                url: '/reports',
              },
            ],
          },
          // ERP integration requests (backend docs/features/erp-integration-requests):
          // main instance + allowlisted white-labels only (D2).
          ...(isErpIntegrationAvailable(brand.key)
            ? [
                {
                  key: 'connect',
                  label: section('CONNECT'),
                  items: [
                    {
                      label: t('nav.declarant.erpIntegration', 'ERP integration'),
                      icon: faIcons.faPlug,
                      url: '/erp-integration',
                      new: true,
                    },
                  ],
                },
              ]
            : []),
        ],
      };
    }

    if (userType === OrganizationType.Consultant) {
      return {
        pinned: [
          {
            label: t('nav.consultant.overview', 'Overview'),
            icon: faIcons.faHome,
            url: '/consultant-overview',
            // Recently opened clients, nested under the home that lists them.
            renderBelow: (collapsed) => <RecentClientsNav isCollapsed={collapsed} />,
          },
          {
            label: t('nav.consultant.certificatePriceTracker', 'Certificate price tracker'),
            icon: faIcons.faChartLine,
            url: '/carbon-price-tracker',
          },
        ],
        sections: [],
      };
    }

    // Operator. Not a translated surface in its own right (operator pages stay
    // English for now) but the sidebar is shared chrome, so it comes along.
    return {
      pinned: [
        { label: t('nav.operator.home', 'Home'), icon: faIcons.faHome, url: '/operator-overview' },
        {
          label: t('nav.operator.uploadHub', 'Upload hub'),
          icon: faIcons.faCloudUploadAlt,
          url: '/stock/upload-hub',
          new: true,
        },
        {
          label: t('nav.operator.downloadHub', 'Download hub'),
          icon: faIcons.faCloudDownloadAlt,
          url: '/stock/download-hub',
          new: true,
        },
      ],
      sections: [
        {
          key: 'manage',
          label: section('MANAGE'),
          items: [
            {
              label: t('nav.operator.suppliers', 'Suppliers'),
              icon: faIcons.faUsers,
              url: '/stock/suppliers',
            },
            {
              label: t('nav.operator.installations', 'Installations'),
              icon: faIcons.faIndustry,
              url: '/operator-installations',
            },
            {
              label: t('nav.operator.purchases', 'Purchases'),
              icon: faIcons.faShoppingCart,
              url: '/stock/purchases',
            },
            {
              label: t('nav.operator.inventory', 'Inventory'),
              icon: faIcons.faBoxes,
              url: '/inventory/lots',
            },
            {
              label: t('nav.operator.sales', 'Sales'),
              icon: faIcons.faMoneyBillWave,
              url: '/stock/sales',
            },
            {
              label: t('nav.operator.reports', 'Reports'),
              icon: faIcons.faFileAlt,
              url: '/stock/reports',
            },
            {
              label: t('nav.operator.verification', 'Verification'),
              icon: faIcons.faShieldAlt,
              url: '/verification',
              new: true,
            },
          ],
        },
        {
          key: 'analyze',
          label: section('ANALYZE'),
          items: [
            {
              label: t('nav.operator.analyticsOverview', 'Overview'),
              icon: faIcons.faChartPie,
              url: '/operator-analytics',
            },
            {
              label: t('nav.operator.certificatePrice', 'Certificate price'),
              icon: faIcons.faEuroSign,
              url: '/carbon-price-tracker',
            },
            {
              label: t('nav.operator.costRadar', 'Cost radar'),
              icon: faIcons.faBullseye,
              url: '/cost-radar',
              new: true,
            },
            {
              label: t('nav.operator.premiums', 'Premiums'),
              icon: faIcons.faAward,
              url: '/operator-premiums',
              new: true,
            },
          ],
        },
        {
          key: 'connect',
          label: section('CONNECT'),
          items: [
            {
              label: t('nav.operator.connections', 'Connections'),
              icon: faIcons.faLink,
              url: '/connections/connected',
            },
            {
              label: t('nav.operator.requests', 'Requests'),
              icon: faIcons.faEnvelope,
              url: '/connections/invites',
              notification: baseInvitesNotification,
            },
            {
              label: t('nav.operator.portal', 'Portal'),
              icon: faIcons.faGlobe,
              url: '/public-catalog',
              new: true,
            },
          ],
        },
      ],
    };
  };

  // `language` is a dependency even though it is not read directly: every
  // label above comes from t(), whose output changes with the language while
  // its arguments do not.
  const menuConfig = useMemo(() => {
    const config = getMenuConfig();
    // Pinned items are the same in both regimes; only the sections wait. This
    // also keeps the other regime's routes out of the search palette.
    return isRegimePending ? { ...config, sections: [] } : config;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRegimePending,
    userType,
    inviteData,
    teamManagementLocked,
    hasFreeSupplierPlan,
    supplierInbox,
    me,
    declarant,
    declarant?.isCustomsRepresentative,
    brand.key,
    language,
  ]);

  // Flat list of every route for the search palette.
  const searchableRoutes = useMemo(() => {
    const out = [];
    menuConfig.pinned.forEach((m) => out.push(m));
    menuConfig.sections.forEach((s) => s.items.forEach((it) => out.push(it)));
    return out;
  }, [menuConfig]);

  // === Collapsible-section state ==================================================
  const [sectionsCollapsed, setSectionsCollapsed] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SECTIONS_KEY) || '{}');
      return saved && typeof saved === 'object' ? saved : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    localStorage.setItem(SECTIONS_KEY, JSON.stringify(sectionsCollapsed));
  }, [sectionsCollapsed]);
  const toggleSection = (key) => setSectionsCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  // One item lights up: when a nested item's url also matches the path, the
  // longest match wins.
  const isPathActive = useCallback(
    (path) => {
      const matches = (p) =>
        !!p && (location.pathname === p || location.pathname.startsWith(`${p}/`));
      if (!matches(path)) return false;
      return !searchableRoutes.some(
        (r) => r.url !== path && r.url?.startsWith(`${path}/`) && matches(r.url),
      );
    },
    [location.pathname, searchableRoutes],
  );
  // A menu item may own its active match (e.g. verifier Engagements, whose url prefixes the others).
  const isItemActive = (item) =>
    item.isActive ? item.isActive(location.pathname) : isPathActive(item.url);

  // === Consultant back button =====================================================
  const shouldShowConsultantBackButton = useMemo(
    () => consultantOrgId && userType !== OrganizationType.Consultant,
    [consultantOrgId, userType],
  );
  const handleBackToConsultant = async () => {
    if (!consultantOrgId) return;
    // performOrgSwitch owns the loader, the already-active guard (a stale
    // persisted consultantOrgId must not strand the loader) and the manual-nav
    // claim; OrgBoundary refreshes the token, clears the cache and remounts —
    // no full-page reload needed.
    await performOrgSwitch({
      setActive,
      orgId: consultantOrgId,
      activeOrgId: organization?.id,
      // navigateTo must match the path the navigate thunk pushes to: OrgBoundary
      // holds the switch loader up until the router reaches it, so the previous
      // (operator) route never renders against the consultant org mid-switch.
      navigateTo: '/consultant-overview',
      navigate: () => {
        clearConsultantOrgId();
        history.push('/consultant-overview');
      },
    });
  };

  // === Contact support ============================================================
  // Sidebar footer entry point into the shared ContactSupportModal — the
  // same support surface the header help dropdown's "Contact Us" opens.
  const [supportOpen, setSupportOpen] = useState(false);

  // === Org switcher ===============================================================
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  // "Manage organization" opens the org-profile surface (Members + our custom
  // Info/Preferences/Roles pages) in a modal — these used to live on the global
  // Header's <OrganizationSwitcher>, which the header-less layout dropped.
  // Its open state is app-wide (orgProfileStore) so a page can deep-link into it.
  const openOrgProfile = useOrgProfileStore((state) => state.openOrgProfile);
  const handleSelectOrg = async (orgId) => {
    setOrgMenuOpen(false);
    // performOrgSwitch shows the loader immediately, guards the already-active
    // org (authoritative Clerk id — the hook can lag), and rolls back on
    // failure. No reload / manual push: OrgBoundary reacts to the org change —
    // it refreshes the backend token, clears the query cache, redirects to the
    // new org's overview, remounts the tree, and clears the loader. Hard-
    // reloading here is the UX regression we removed.
    await performOrgSwitch({ setActive, orgId, activeOrgId: organization?.id });
  };
  const handleManageOrg = () => {
    openOrgProfile();
    setOrgMenuOpen(false);
  };
  const handleSearchSelect = (url) => {
    history.push(url);
    onPrefetchRoute?.(url);
  };

  // === Render =====================================================================
  const sidebarWidth = isCollapsed ? `${SIDEBAR_COLLAPSED_WIDTH}px` : `${SIDEBAR_WIDTH}px`;
  const sidebarPosition = isSidebarHidden && window.innerWidth <= 1024 ? '-100%' : '0';
  // Header is suppressed for this layout, so the sidebar starts at the top
  // of the viewport (+ freemium banner + background-jobs banner when
  // present). Total subtracted from 100vh tracks the same.
  const topOffset = hasExceededLimit
    ? 'calc(32px + var(--cg-jobs-banner-h, 0px))'
    : 'calc(0px + var(--cg-jobs-banner-h, 0px))';
  const sidebarHeight = hasExceededLimit
    ? 'calc(100vh - 32px - var(--cg-jobs-banner-h, 0px))'
    : 'calc(100vh - 0px - var(--cg-jobs-banner-h, 0px))';

  const renderSection = (section) => {
    const open = !sectionsCollapsed[section.key];
    return (
      <div
        key={section.key}
        className="mt-1"
      >
        {!isCollapsed && (
          <SectionHeader
            label={section.label}
            count={section.items.length}
            isOpen={open}
            onToggle={() => toggleSection(section.key)}
          />
        )}
        {isCollapsed && <CollapsedDivider />}
        {(isCollapsed || open) && (
          <div className={`${isCollapsed ? 'space-y-0.5' : 'space-y-0.5 mt-0.5'}`}>
            {section.items.map((item) => (
              <NavItem
                key={item.url}
                icon={item.icon}
                label={item.label}
                url={item.url}
                isActive={isItemActive(item)}
                isCollapsed={isCollapsed}
                notification={item.notification}
                badge={item.badge}
                isNew={item.new}
                onMouseEnter={() => onPrefetchRoute?.(item.url)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{`
        .cg-nav-sidebar [data-nav-item] {
          font-size: 13px;
          padding-top: 6px;
          padding-bottom: 6px;
        }
        @media (max-height: 820px) {
          .cg-nav-sidebar [data-nav-item] {
            padding-top: 5px;
            padding-bottom: 5px;
          }
          .cg-nav-sidebar [data-section-header] {
            margin-top: 6px !important;
          }
        }
        @media (max-height: 720px) {
          .cg-nav-sidebar [data-nav-item] {
            font-size: 12px;
            padding-top: 4px;
            padding-bottom: 4px;
          }
          .cg-nav-sidebar [data-section-header] {
            margin-top: 4px !important;
            padding-top: 3px !important;
            padding-bottom: 3px !important;
          }
          .cg-nav-sidebar [data-nav-icon] {
            font-size: 13px;
          }
        }
        @media (max-height: 620px) {
          .cg-nav-sidebar [data-nav-item] {
            font-size: 11px;
            padding-top: 3px;
            padding-bottom: 3px;
          }
          .cg-nav-sidebar [data-section-header] span {
            font-size: 9px;
          }
        }
      `}</style>
      <div
        className="cg-nav-sidebar fixed top-0 z-[100] bg-white border-r border-slate-200 flex flex-col"
        style={{
          width: sidebarWidth,
          left: SIDEBAR_LEFT_MARGIN,
          transform: `translateX(${sidebarPosition})`,
          marginTop: topOffset,
          height: `calc(${sidebarHeight} - ${SIDEBAR_LEFT_MARGIN}px)`,
          transition: 'width 0.2s ease',
        }}
      >
        {/* Header: brand logo + collapse toggle. Logo doubles as a Home
          link so the dedicated /overview shortcut still resolves with a
          single click. */}
        <div
          className={`flex items-center border-b border-slate-100 ${
            isCollapsed ? 'justify-center px-0 py-2' : 'px-3 py-2'
          }`}
        >
          {/* Collapsed rail shows the favicon rather than squeezing the full
              wordmark into 28px. Whitelabels have no dedicated mini-logo, but
              their favicon is already brand-specific (dedicated build, Faradai
              host, or embed override), so it's the right mini mark for them
              too. */}
          <button
            type="button"
            onClick={() => history.push('/')}
            className={`flex items-center min-w-0 ${isCollapsed ? 'justify-center' : 'flex-1'}`}
            aria-label={t('nav.goHome', 'Go home')}
          >
            {isWhitelabel && !brand.logoUrl && !isCollapsed ? (
              // Whitelabel without a logo asset: show the brand NAME rather
              // than falling back to the CG wordmark.
              <span className="text-sm font-semibold text-slate-800 truncate">{brand.name}</span>
            ) : (
              <img
                src={
                  isCollapsed ? brand.faviconUrl || '/favicon.ico' : brand.logoUrl || logoFallback
                }
                alt={brand.name || 'Carbon Glance'}
                className={`object-contain ${isCollapsed ? 'h-7 w-7' : 'h-8'}`}
                style={isCollapsed ? { maxWidth: 28 } : { maxWidth: 140 }}
              />
            )}
          </button>
          <button
            type="button"
            onClick={onToggleCollapse}
            title={
              isCollapsed
                ? t('nav.expandSidebar', 'Expand sidebar')
                : t('nav.collapseSidebar', 'Collapse sidebar')
            }
            aria-label={
              isCollapsed
                ? t('nav.expandSidebar', 'Expand sidebar')
                : t('nav.collapseSidebar', 'Collapse sidebar')
            }
            className={`${isCollapsed ? 'hidden' : 'ml-auto'} w-6 h-6 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors`}
          >
            <FontAwesomeIcon
              icon={faIcons.faAnglesLeft}
              className="text-[12px]"
            />
          </button>
        </div>
        {/* Floating expand toggle for the collapsed state so it doesn't push
          the logo off-center. */}
        {isCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={t('nav.expandSidebar', 'Expand sidebar')}
            className="absolute -right-3 top-12 z-10 w-6 h-6 inline-flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300 shadow-sm"
          >
            <FontAwesomeIcon
              icon={faIcons.faAnglesRight}
              className="text-[10px]"
            />
          </button>
        )}

        {/* Search — hidden entirely in the collapsed rail so it stays pure
          navigation glyphs (no search affordance). */}
        {!isCollapsed && (
          <div className="px-3 pt-2 pb-1">
            <SearchBar
              allRoutes={searchableRoutes}
              isCollapsed={isCollapsed}
              onSelect={handleSearchSelect}
            />
          </div>
        )}

        {/* Nav body */}
        <nav className={`flex-1 overflow-y-auto scrollbar-hide ${isCollapsed ? 'px-1' : 'px-2'}`}>
          {/* Pinned items */}
          {menuConfig.pinned.length > 0 && (
            <div className={isCollapsed ? 'space-y-0.5 pt-1' : 'space-y-0.5 pt-1.5'}>
              {menuConfig.pinned.map((item) => (
                <React.Fragment key={item.url}>
                  <NavItem
                    icon={item.icon}
                    label={item.label}
                    url={item.url}
                    isActive={isItemActive(item)}
                    isCollapsed={isCollapsed}
                    notification={item.notification}
                    isNew={item.new}
                    onMouseEnter={() => onPrefetchRoute?.(item.url)}
                  />
                  {item.renderBelow?.(isCollapsed)}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Collapsible workflow sections */}
          {menuConfig.sections.map(renderSection)}
          {isRegimePending && <NavSectionsSkeleton isCollapsed={isCollapsed} />}
        </nav>

        {/* Back-to-consultant */}
        {shouldShowConsultantBackButton && (
          <div className={`border-t border-slate-100 ${isCollapsed ? 'px-1 py-2' : 'px-2 py-2'}`}>
            <button
              type="button"
              onClick={handleBackToConsultant}
              title={t('nav.backToConsultantView', 'Back to consultant view')}
              className={`w-full flex items-center rounded-lg text-[12px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-100 transition-colors ${isCollapsed ? 'justify-center py-2 px-0' : 'gap-2 px-2.5 py-2'}`}
            >
              <FontAwesomeIcon
                icon={faIcons.faArrowLeft}
                className="text-[12px]"
              />
              {!isCollapsed && (
                <span className="truncate">{t('nav.consultantView', 'Consultant view')}</span>
              )}
            </button>
          </div>
        )}

        {/* Trial banner — always visible (was: only when expanded) */}
        {!isCollapsed && (
          <div className="px-3 pt-2">
            <TrialBanner />
          </div>
        )}

        {/* Utility band: language switcher + Contact Support, sitting directly
          above the org/user footer. Language first — the sidebar is this
          app's only persistent chrome, so this is where the conventional
          "top-right globe" lives (one click, visible on every page; the
          Preferences tab keeps the full-size field). Collapsed-aware like the
          rest of the rail. The switcher is absent for the org types pinned to
          English (operator/consultant) — see languageAccess. */}
        <div className={`border-t border-slate-100 ${isCollapsed ? 'px-1 py-2' : 'px-2 py-2'}`}>
          {isLanguageSelectionAllowed(userType) && (
            <NavLanguageSwitcher isCollapsed={isCollapsed} />
          )}
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            title={isCollapsed ? t('nav.contactSupport', 'Contact Support') : undefined}
            aria-label={t('nav.contactSupport', 'Contact Support')}
            className={`w-full flex items-center rounded-lg text-[12px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors ${isCollapsed ? 'justify-center py-2 px-0' : 'gap-2 px-2.5 py-2'}`}
          >
            <FontAwesomeIcon
              icon={faIcons.faCircleQuestion}
              className="text-[12px]"
            />
            {!isCollapsed && (
              <span className="truncate">{t('nav.contactSupport', 'Contact Support')}</span>
            )}
          </button>
        </div>

        {/* Footer: org switcher + user row */}
        <div className="mt-auto border-t border-slate-100">
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-1 py-2">
              <CgPopover
                open={orgMenuOpen}
                onOpenChange={setOrgMenuOpen}
                trigger="click"
                position="right"
                sideOffset={6}
                triggerElement={
                  <button
                    type="button"
                    title={organization?.name || t('nav.orgSwitcher.organization', 'Organization')}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-slate-50 transition-colors"
                  >
                    <OrgAvatar
                      org={organization}
                      size={26}
                    />
                  </button>
                }
                popoverContent={
                  <OrgSwitcherMenu
                    active={organization}
                    memberships={userMemberships?.data || []}
                    hasNextPage={userMemberships?.hasNextPage}
                    fetchNext={userMemberships?.fetchNext}
                    onSelect={handleSelectOrg}
                    onManage={handleManageOrg}
                  />
                }
              />
              <UserMenu isCollapsed />
            </div>
          ) : (
            <div className="p-2 space-y-1">
              <CgPopover
                open={orgMenuOpen}
                onOpenChange={setOrgMenuOpen}
                trigger="click"
                position="top"
                sideOffset={6}
                triggerElement={
                  <button
                    type="button"
                    className="w-full flex items-center gap-2.5 px-2 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-left transition-colors"
                  >
                    <OrgAvatar
                      org={organization}
                      size={28}
                    />
                    <div className="min-w-0 flex-1 leading-tight">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 m-0">
                        {t('nav.orgSwitcher.organization', 'Organization')}
                      </p>
                      <p className="text-[13px] font-semibold text-slate-900 m-0 truncate">
                        {organization?.name ||
                          t('nav.orgSwitcher.noOrganization', 'No organization')}
                      </p>
                    </div>
                    <FontAwesomeIcon
                      icon={faIcons.faSort}
                      className="text-slate-400 text-[10px] shrink-0"
                    />
                  </button>
                }
                popoverContent={
                  <OrgSwitcherMenu
                    active={organization}
                    memberships={userMemberships?.data || []}
                    hasNextPage={userMemberships?.hasNextPage}
                    fetchNext={userMemberships?.fetchNext}
                    onSelect={handleSelectOrg}
                    onManage={handleManageOrg}
                  />
                }
              />
              <UserMenu />
            </div>
          )}
        </div>
      </div>
      <ContactSupportModal
        show={supportOpen}
        onHide={() => setSupportOpen(false)}
        supportEmail={brand.supportEmail}
        brandName={brand.name}
        isWhitelabel={isWhitelabel}
        primaryColor={brand.primaryColor}
      />
      <OrgProfileHost />
    </>
  );
};
